import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Header from "@/sections/Header";
import Footer from "@/sections/Footer";
import VerifiedBadge from "@/components/VerifiedBadge";
import { useAuth } from "@/contexts/AuthContext";
import { getExistingSubscription } from "@/lib/push";
import { BellOff, Eye, Coins, Users, Zap, BellRing } from "lucide-react";

const LEVEL_COLOR = { verified: "#00ff41", building: "#cfff04", unverified: "#ff3333" };
const ago = (ts) => {
  const d = Math.floor((Date.now() / 1000 - ts) / 86400);
  return d <= 0 ? "today" : d === 1 ? "yesterday" : `${d}d ago`;
};

function Stat({ icon: Icon, value, label, accent }) {
  return (
    <div className="flex items-center gap-1.5 font-mono text-[11px]" data-testid={`wl-stat-${label}`}>
      <Icon size={11} className="text-[#b8b8b8]" />
      <span className={accent || "text-white"}>{value}</span>
      <span className="text-[#b8b8b8] uppercase tracking-widest text-[10px]">{label}</span>
    </div>
  );
}

function Row({ c, onUnwatch }) {
  const w = c.week;
  const hd = w.holders_delta;
  return (
    <div className="border border-[#3a3a3a] bg-[#121212] p-5 grid md:grid-cols-12 gap-4 items-start" data-testid={`wl-row-${c.slug}`}>
      <Link to={`/c/${c.slug}`} className="md:col-span-4 flex items-center gap-4 group">
        <div className="w-12 h-12 border border-[#3a3a3a] bg-[#050505] overflow-hidden flex-shrink-0">
          <img src={`${process.env.REACT_APP_BACKEND_URL || ""}/api/coins/${c.slug}/logo`} alt="" className="w-full h-full object-cover"
               onError={(e) => { e.currentTarget.style.display = "none"; }} />
        </div>
        <div className="min-w-0">
          <div className="font-display font-black uppercase tracking-tight truncate group-hover:text-[#cfff04]">{c.name}</div>
          <div className="font-mono text-[11px] text-[#d0d0d0] flex items-center gap-2">${c.ticker} <VerifiedBadge coin={c} compact /></div>
        </div>
      </Link>
      <div className="md:col-span-2">
        <div className="font-mono text-2xl font-bold" style={{ color: LEVEL_COLOR[c.trust.level] }} data-testid={`wl-trust-${c.slug}`}>{c.trust.score}</div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#b8b8b8]">trust · {c.trust.passed}/{c.trust.total}</div>
      </div>
      <div className="md:col-span-5 space-y-2">
        <div className="flex flex-wrap gap-4">
          <Stat icon={Eye} value={w.views} label="views" />
          <Stat icon={Coins} value={w.tips ? `${w.tips} · ${w.tips_sol} SOL` : "0"} label="tips" accent={w.tips ? "text-[#00ff41]" : "text-white"} />
          {c.holders != null && (
            <Stat icon={Users} value={hd == null ? c.holders : `${c.holders} ${hd >= 0 ? "▲" : "▼"}${Math.abs(hd)}`} label="holders"
                  accent={hd > 0 ? "text-[#00ff41]" : hd < 0 ? "text-[#ff3333]" : "text-white"} />
          )}
        </div>
        {w.events.length > 0 ? (
          <ul className="space-y-1" data-testid={`wl-events-${c.slug}`}>
            {w.events.map((e, i) => (
              <li key={i} className="font-mono text-[11px] text-[#e2e2e2] flex items-center gap-2">
                <Zap size={10} className="text-[#cfff04]" /> {e.title} <span className="text-[#b8b8b8]">· {ago(e.ts)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="font-mono text-[11px] text-[#b8b8b8]">Quiet week — no alerts fired.</div>
        )}
      </div>
      <div className="md:col-span-1 flex md:justify-end">
        <button onClick={() => onUnwatch(c.slug)} data-testid={`wl-unwatch-${c.slug}`} title="Stop watching"
                className="border border-[#3a3a3a] hover:border-[#ff3333] hover:text-[#ff3333] text-[#b8b8b8] p-2">
          <BellOff size={13} />
        </button>
      </div>
    </div>
  );
}

/** /watchlist — everything a buyer follows, with trust score and this week's changes. */
export default function Watchlist() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [endpoint, setEndpoint] = useState(null);

  const load = useCallback(async () => {
    const sub = await getExistingSubscription().catch(() => null);
    setEndpoint(sub?.endpoint || null);
    const q = sub?.endpoint ? `?endpoint=${encodeURIComponent(sub.endpoint)}` : "";
    api.get(`/me/watchlist${q}`).then((r) => setData(r.data)).catch(() => setData({ coins: [], signed_in: false }));
  }, []);

  useEffect(() => { load(); }, [load, user?.user_id]);

  const unwatch = async (slug) => {
    const q = endpoint ? `?endpoint=${encodeURIComponent(endpoint)}` : "";
    await api.delete(`/watch/${slug}${q}`).catch(() => {});
    toast("Stopped watching $" + slug.toUpperCase());
    load();
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />
      <main className="pt-24 pb-20" data-testid="watchlist-page">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#cfff04] mb-3 flex items-center gap-2"><BellRing size={12} /> // Your watchlist</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tighter mb-3">What moved this week.</h1>
          <p className="text-[#e2e2e2] mb-10 max-w-2xl">
            Every coin you follow, its live trust score, and the last 7 days of views, tips, holder growth and alerts.
          </p>

          {data === null ? (
            <div className="h-40 border border-[#3a3a3a] animate-pulse" />
          ) : data.coins.length === 0 ? (
            <div className="border border-[#3a3a3a] bg-[#121212] p-10 text-center" data-testid="watchlist-empty">
              <h2 className="font-display font-black uppercase tracking-tight text-lg md:text-lg mb-2">You're not watching anything yet.</h2>
              <p className="text-[#e2e2e2] text-sm max-w-md mx-auto mb-6">
                Hit <span className="font-mono text-[#cfff04]">Watch</span> on any coin page to get alerts for whale tips, mints and pool launches — it shows up here.
                {!data.signed_in && " Sign in to keep your watchlist across devices."}
              </p>
              <Link to="/explore" data-testid="watchlist-explore" className="btn-brutal inline-flex bg-[#cfff04] text-[#050505] font-bold uppercase tracking-wider text-sm px-8 py-4">Explore coins</Link>
            </div>
          ) : (
            <div className="space-y-3" data-testid="watchlist-rows">
              {data.coins.map((c) => <Row key={c.slug} c={c} onUnwatch={unwatch} />)}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
